class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);
    this.querySelector('form').addEventListener(
      'input',
      this.debouncedOnSubmit.bind(this),
    );
  }

  connectedCallback() {
    window.addEventListener('scroll', () => {
      const topOffset = this.getBoundingClientRect().top;

      this.classList.toggle('is-sticky', topOffset < 0);
    });

    // Initialize click-outside-to-close for filter details
    // Only initialize once per instance
    if (!this.facetDetailsClickOutsideInitialized) {
      this.facetDetailsClickOutsideInitialized = true;
      this.onDocumentClick = this.onDocumentClick.bind(this);
      // Use capture phase to catch clicks before they bubble
      document.addEventListener('click', this.onDocumentClick, true);
    }
  }

  disconnectedCallback() {
    // Clean up event listener when component is removed
    if (this.onDocumentClick) {
      document.removeEventListener('click', this.onDocumentClick, true);
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);

      if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    });
  }

  static renderAvailabilityFilter(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const availabilityProxyLabel = document.querySelector('availability-proxy label');
    const availabilityProxyInput = document.querySelector('availability-proxy input');

    const availabilityCheckbox = parsedHTML.querySelector('facet-filters-form input[name="filter.v.availability"]').nextElementSibling.innerHTML;
    const availabilityInput = parsedHTML.querySelector('facet-filters-form input[name="filter.v.availability"]');
    availabilityInput;

    availabilityProxyLabel.innerHTML = availabilityCheckbox;
    availabilityProxyInput.checked = availabilityInput.checked;
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.renderAvailabilityFilter(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.renderAvailabilityFilter(html);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    if (!document.getElementById('ProductGridPagination')) return;

    document.getElementById('ProductGridPagination').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridPagination').innerHTML;
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    // Update ProductCount (desktop)
    const count = parsedHTML.getElementById('ProductCount');
    const container = document.getElementById('ProductCount');

    if (container) {
      container.innerHTML = count ? count.innerHTML : '';
    }

    // Update ProductCountmobile (mobile)
    const countMobile = parsedHTML.getElementById('ProductCountmobile');
    const containerMobile = document.getElementById('ProductCountmobile');

    if (containerMobile) {
      containerMobile.innerHTML = countMobile ? countMobile.innerHTML : '';
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter',
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element),
    );
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const elem = document.querySelector(
        `.js-filter[data-index="${element.dataset.index}"]`,
      )
      if (elem) {
        elem.innerHTML = element.innerHTML;
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);

    if (countsToRender)
      FacetFiltersForm.renderCounts(
        countsToRender,
        event.target.closest('.js-filter'),
      );
  }

  static renderActiveFacets(html) {
    ['.js-facets-active', '.js-facets-remove-all'].forEach((element) => {
      const htmlElement = html.querySelector(element);
      const hostElement = document.querySelector(element);

      if (!htmlElement || !hostElement) {
        return;
      }

      hostElement.outerHTML = htmlElement.outerHTML;
    });
  }

  static renderCounts(source, target) {
    const selector = '.js-facets-selected';
    const targetElement = target.querySelector(selector);
    const sourceElement = source.querySelector(selector);

    if (sourceElement && targetElement) {
      target.querySelector(selector).outerHTML =
        source.querySelector(selector).outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`,
    );
  }

  static getSections() {
    return [
      {
        section: document.querySelector('facet-filters-form').dataset.sectionId,
      },
    ];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const context = event.target.id.includes('desktop') ? 'mobile' : 'desktop';
    const formData = new FormData();
    const formDataElements = Array.from(
      event.target.closest('form').elements,
    ).filter(
      (element) => !element.id.includes(context) || element.name === 'sort_by',
    );
    for (const element of formDataElements) {
      if (!element.name) continue;
      if (element.type === 'checkbox') {
        if (element.checked) {
          formData.append(element.name, element.value);
        }
      } else if (element.type === 'radio') {
        // Only include checked radio buttons
        if (element.checked) {
          formData.append(element.name, element.value);
        }
      } else {
        formData.append(element.name, element.value);
      }
    }
    const searchParams = new URLSearchParams(formData).toString();
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf('?') + 1,
          );
    FacetFiltersForm.renderPage(url);
  }

  /**
   * Handle click-outside-to-close for individual filter details elements
   * Closes filter details when clicking outside, but keeps them open
   * when clicking on filter elements inside (checkboxes, radios, inputs, etc.)
   * Excludes sort dropdown which is inside dropdown-input
   */
  onDocumentClick(event) {
    // Find all open filter details elements (js-filter) that are NOT inside dropdown-input
    const openFilterDetails = document.querySelectorAll('details.js-filter[open]');

    openFilterDetails.forEach((detailsElement) => {
      // Skip if inside dropdown-input (sort dropdown)
      if (detailsElement.closest('dropdown-input')) {
        return;
      }

      const target = event.target;
      const summaryElement = detailsElement.querySelector('summary');

      // Don't close if clicking on the summary (native behavior handles toggle)
      if (target === summaryElement || summaryElement.contains(target)) {
        return;
      }

      // Don't close if clicking inside the details element
      if (detailsElement.contains(target)) {
        return;
      }

      // Don't close if clicking on the availability filter
      if (detailsElement.classList.contains('facet--availability')) {
        return;
      }

      // Don't close if clicking on the availability filter in the sidebar only
      if (detailsElement.closest('.collection__sidebar-filter')) {
        return;
      }

      // Click is outside the details element - close it
      this.closeDetails(detailsElement, summaryElement);
    });
  }

  closeDetails(detailsElement, summaryElement) {
    if (detailsElement.classList.contains('facet--mobile')) {
      return;
    }

    if (summaryElement) {
      summaryElement.setAttribute('aria-expanded', 'false');
      detailsElement.removeAttribute('open');
    }
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.inputs = this.querySelectorAll('input[type="number"]');
    this.radioButtons = this.querySelectorAll('input[type="radio"][data-min][data-max]');

    this.inputs.forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this)),
    );

    this.radioButtons.forEach((element) =>
      element.addEventListener('change', this.onRadioChange.bind(this)),
    );

    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    this.updateRadioSelection();
  }

  onRadioChange(event) {
    const radio = event.currentTarget;
    const minValue = radio.getAttribute('data-min');
    const maxValue = radio.getAttribute('data-max');
    const minInput = this.inputs[0];
    const maxInput = this.inputs[1];

    // Get currency decimal format from the price-range element
    const usesCommaDecimals = this.dataset.usesCommaDecimals === 'true';

    // Convert cents to display value
    const minDisplay = (parseFloat(minValue) / 100).toFixed(2);
    const maxDisplay = (parseFloat(maxValue) / 100).toFixed(2);

    // Format based on decimal separator
    let minFormatted = minDisplay;
    let maxFormatted = maxDisplay;

    if (usesCommaDecimals) {
      // minFormatted = minDisplay.replace('.', ',');
      // maxFormatted = maxDisplay.replace('.', ',');
    } else {
      // minFormatted = minDisplay.replace(',', '');
      // maxFormatted = maxDisplay.replace(',', '');
    }

    // Update input values
    minInput.value = minFormatted;
    maxInput.value = maxFormatted;

    // Trigger form submission
    const form = this.closest('form');
    if (form) {
      const formData = new FormData(form);
      const searchParams = new URLSearchParams(formData).toString();
      FacetFiltersForm.renderPage(searchParams, event);
    }
  }

  updateRadioSelection() {
    const minInput = this.inputs[0];
    const maxInput = this.inputs[1];
    if (!minInput.value || !maxInput.value) {
      this.radioButtons.forEach((radio) => {
        radio.checked = false;
      });
      return;
    }

    const minValue = parseFloat(minInput.value.replace(',', '.')) * 100;
    const maxValue = parseFloat(maxInput.value.replace(',', '.')) * 100;

    // Uncheck all radios first
    this.radioButtons.forEach((radio) => {
      radio.checked = false;
    });

    // Check matching radio if values match exactly
    this.radioButtons.forEach((radio) => {
      const radioMin = parseFloat(radio.getAttribute('data-min'));
      const radioMax = parseFloat(radio.getAttribute('data-max'));
      if (Math.abs(minValue - radioMin) < 1 && Math.abs(maxValue - radioMax) < 1) {
        radio.checked = true;
      }
    });
  }

  setMinAndMaxValues() {
    const minInput = this.inputs[0];
    const maxInput = this.inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '')
      minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class AvailabilityProxy extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.label = this.querySelector('label');
    this.input.addEventListener('change', (event) => {
      document.querySelector(`facet-filters-form input[name="${this.input.name}"]`).click();
    });
  }
}

customElements.define('availability-proxy', AvailabilityProxy);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelector('a').addEventListener('click', (event) => {
      event.preventDefault();
      const form =
        this.closest('facet-filters-form') ||
        document.querySelector('facet-filters-form');
      form.onActiveFilterClick(event);
    });
  }
}

customElements.define('facet-remove', FacetRemove);

